import useOutsideClick from '@hero/ui-kit/hooks/useOutsideClick';
import CaretIcon from '@hero/ui-kit/icons/utility/CaretIcon';
import CaretButton from '@hero/ui-kit/inputs/CaretButton';
import Section from '@hero/ui-kit/layout/Section';
import React from 'react';
import * as Style from './style.module.scss';

interface FilterProps {
    filterLabel: string | React.ReactElement;
    children: React.ReactNode;
    ignoreClickOutside?: boolean;
    width?: string;
    className?: string;
    dropdownPosition?: 'left' | 'right';
    activeClassName?: string;
    hasFilters?: boolean;
    minimal?: boolean;
    disabled?: boolean;
    // When true, render as a collapsible accordion section instead of a
    // toggleable dropdown — used to compose several filters inside one shared
    // popover (e.g. the header search filters panel).
    inline?: boolean;
    heading?: string;
    // Number of active selections in this facet — shown as a small count badge
    // next to the inline section heading.
    count?: number;
    // When provided (inline mode), renders a small "Clear" link in the section
    // title bar next to the caret — only while `count` shows something to clear.
    onShowAll?: () => void;
}

type InlineFilterProps = Pick<
    FilterProps,
    | 'filterLabel'
    | 'children'
    | 'className'
    | 'hasFilters'
    | 'disabled'
    | 'heading'
    | 'count'
    | 'onShowAll'
>;

// Section variant used inside the shared header-search filters panel: a
// persistent category heading with the filter content shown by default. The
// heading stays clickable so a long facet can be collapsed, but the panel no
// longer hides every filter behind a closed accordion.
const InlineFilter: React.FC<InlineFilterProps> = ({
    children,
    className = '',
    hasFilters = false,
    disabled = false,
    heading,
    filterLabel,
    count = 0,
    onShowAll
}) => {
    const [expanded, setExpanded] = React.useState(true);
    const toggle = () => setExpanded((current) => !current);
    // The header always shows the static category name; the selected values are
    // conveyed by the rows themselves and a count badge on the heading.
    const label = heading ?? filterLabel;

    return (
        <div className={`${Style.inlineSection} ${disabled ? Style.disabled : ''} ${className}`}>
            <div className={Style.inlineHeader}>
                <button
                    type="button"
                    aria-expanded={expanded}
                    className={`${Style.inlineToggle} ${
                        hasFilters ? Style.inlineToggleActive : ''
                    }`}
                    onClick={toggle}
                >
                    <span className={Style.inlineHeading}>
                        {label}
                        {count > 0 ? <span className={Style.count}>{count}</span> : null}
                    </span>
                </button>
                <div className={Style.inlineHeaderRight}>
                    {onShowAll && count > 0 ? (
                        <button type="button" className={Style.showAll} onClick={onShowAll}>
                            Clear
                        </button>
                    ) : null}
                    <button
                        type="button"
                        aria-label={expanded ? 'Collapse' : 'Expand'}
                        className={Style.caretButton}
                        onClick={toggle}
                    >
                        <CaretIcon size="small" direction={expanded ? 'up' : 'down'} />
                    </button>
                </div>
            </div>
            {expanded ? children : null}
        </div>
    );
};

const DropdownFilter: React.FC<FilterProps> = ({
    filterLabel,
    children,
    ignoreClickOutside = false,
    width = '24rem',
    className = '',
    dropdownPosition = 'left',
    activeClassName = Style.activeFilter,
    hasFilters = false,
    minimal = false,
    disabled = false
}) => {
    const [filterStatusExpanded, setFilterStatusExpanded] = React.useState(false);
    const [ref, isOutsideClick] = useOutsideClick(filterStatusExpanded);

    React.useEffect(() => {
        isOutsideClick && !ignoreClickOutside && setFilterStatusExpanded(false);
    }, [isOutsideClick, ignoreClickOutside]);

    const filterPositionClass =
        dropdownPosition === 'left' ? Style.filterDropdownLeft : Style.filterDropdownRight;

    const handleToggleFilter = () => {
        setFilterStatusExpanded((current) => !current);
    };

    const filterElement =
        typeof filterLabel === 'string' ? (
            <CaretButton
                className={`${Style.filterStatusToggle} ${
                    filterStatusExpanded ? Style.filterStatusExpanded : ''
                } ${hasFilters ? activeClassName : ''}`}
                textBefore={filterLabel}
                directionCollapsed="down"
                directionExpanded="up"
                isExpanded={filterStatusExpanded}
                size={minimal ? 'small' : 'utility'}
                onClick={() => {
                    setFilterStatusExpanded((current) => !current);
                }}
            />
        ) : (
            React.cloneElement(filterLabel, {
                onClick: handleToggleFilter,
                className: `${filterLabel.props.className} ${
                    filterStatusExpanded ? Style.filterStatusExpanded : ''
                }`
            })
        );

    return (
        <div
            className={`${Style.filterStatus} ${minimal ? Style.minimal : ''} ${disabled ? Style.disabled : ''} ${className}`}
            // @ts-ignore
            ref={ref}
        >
            {filterElement}
            {filterStatusExpanded && (
                <Section
                    noDefaultPadding
                    className={`${Style.filterStatusDropdown} ${filterPositionClass}`}
                    styles={{ width }}
                >
                    {children}
                </Section>
            )}
        </div>
    );
};

const Filter: React.FC<FilterProps> = (props) =>
    props.inline ? <InlineFilter {...props} /> : <DropdownFilter {...props} />;

export default Filter;
